import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

export interface AuthTokenProvider {
  getAccessToken: () => string | null;
  onRefreshSuccess: (newAccessToken: string) => void;
  onUnauthenticated: () => void;
  refresh: () => Promise<string>;
}

// Extended config type — carries the two flags we stash on retries so both
// interceptors can cooperate without fighting over the Authorization header.
interface AuthRetryConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  // Set by the response interceptor when it injects a fresh Authorization
  // header on a retry. Signals the request interceptor to leave it alone —
  // otherwise the request interceptor would overwrite it with a stale token
  // read from `tokenRef.current`, and we'd get 401 even after a successful
  // refresh (race between React state update and axios retry).
  _authHeaderSet?: boolean;
}

let tokenProvider: AuthTokenProvider | null = null;

export function installAuthTokenProvider(provider: AuthTokenProvider): void {
  tokenProvider = provider;
}

export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

// --- Connection ID injection (Stage 8: multi-connection) ---
// The active connection ID is injected as ?connection_id=<UUID> on every
// request that talks to connection-scoped endpoints. Auth, admin, and
// connections CRUD endpoints are excluded.

let connectionIdProvider: (() => string | null) | null = null;

export function installConnectionIdProvider(fn: () => string | null): void {
  connectionIdProvider = fn;
}

// `/intercompany/` управляет тремя подключениями через `pair_id`, активный
// connection ему не нужен — не подмешиваем `?connection_id` в эти запросы.
// `/onec-bases` (справочник баз SSO) и `/config` (до входа) — не про подключение.
const EXCLUDED_PREFIXES = [
  "/auth/",
  "/admin/",
  "/connections",
  "/intercompany/",
  "/onec-bases",
  "/config",
];

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const cfg = config as AuthRetryConfig;
  if (cfg._authHeaderSet) {
    return cfg;
  }
  const token = tokenProvider?.getAccessToken();
  if (token) {
    cfg.headers.set("Authorization", `Bearer ${token}`);
  }
  // Inject connection_id for scoped endpoints.
  const url = cfg.url ?? "";
  if (
    connectionIdProvider &&
    !EXCLUDED_PREFIXES.some((p) => url.startsWith(p))
  ) {
    const connId = connectionIdProvider();
    if (connId) {
      cfg.params = { ...cfg.params, connection_id: connId };
    }
  }
  return cfg;
});

// --- 401 refresh queue ---
// While a refresh is in-flight, any subsequent 401 waits for the same promise
// instead of triggering parallel refreshes (which would fight over rotation).
let refreshInFlight: Promise<string> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const originalReq = error.config as AuthRetryConfig | undefined;
    if (!originalReq || !error.response) throw error;
    if (error.response.status !== 401) throw error;
    if (originalReq._retried) throw error;
    // Don't try to refresh on auth endpoints themselves.
    const url = originalReq.url ?? "";
    if (url.startsWith("/auth/login") || url.startsWith("/auth/refresh")) {
      throw error;
    }
    if (!tokenProvider) throw error;

    try {
      if (!refreshInFlight) {
        refreshInFlight = tokenProvider.refresh().finally(() => {
          refreshInFlight = null;
        });
      }
      const newToken = await refreshInFlight;
      originalReq._retried = true;
      originalReq._authHeaderSet = true;
      originalReq.headers.set("Authorization", `Bearer ${newToken}`);
      return api.request(originalReq as AxiosRequestConfig);
    } catch (refreshErr) {
      tokenProvider.onUnauthenticated();
      throw refreshErr;
    }
  },
);
